/**
 * Parent-portal admin API client (ADR-0015) — the operator editor for the
 * instance-level **tenant brand** that the launcher + slug-less login wear.
 * Mirrors `backend/src/hub/routes/parent_portal.py`
 * (`/api/admin/parent-portal/brand`, gated by the `parent_portal` resource).
 */
import { api } from "./api";

/** Instance-level tenant brand (mirrors backend `TenantBrand`). */
export interface TenantBrand {
  name: string | null;
  logo_url: string | null;
  /** Free-form theme tokens (e.g. `accent`, `background`). */
  theme: Record<string, string>;
  login: { title: string | null; subtitle: string | null };
  /** Small footer line shown on the portal login + launcher. */
  footer?: string | null;
  /** Master switch for the in-app waffle app-switcher. */
  switcher_enabled: boolean;
}

/** Portal registration policy (mirrors backend `Registration`, ADR-0016).
 *  The single switch for self-signup now that the portal is the only door. */
export interface Registration {
  /** Whether the portal accepts public self-signup at all. */
  enabled: boolean;
  /** 'direct' → account active immediately; 'requires_approval' → pending
   *  until a Tray admin accepts it. */
  mode: "direct" | "requires_approval";
}

/** Portal-realm 2FA enforcement (mirrors backend `Security`, ADR-0017). The
 *  operator flag moved to the Tray users admin (`usersApi.getSecurity`). */
export interface Security {
  totp_required_app_users: boolean;
}

export const parentPortalApi = {
  getBrand: () => api.get<TenantBrand>("/parent-portal/brand"),
  updateBrand: (body: TenantBrand) => api.put<TenantBrand>("/parent-portal/brand", body),
  getRegistration: () => api.get<Registration>("/parent-portal/registration"),
  updateRegistration: (body: Registration) =>
    api.put<Registration>("/parent-portal/registration", body),
  getSecurity: () => api.get<Security>("/parent-portal/security"),
  updateSecurity: (body: Security) => api.put<Security>("/parent-portal/security", body),
};
